import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { createPersistReducer, PersistReducerConfigs } from '@app/persist';
import type { TwoFactorAuthVerifyResponse } from '@services/df/auth';
import { enhancedApi as api } from '@services/df/auth';
import { PasswordlessLogin, StoreResponseErrors } from '@types';

export interface AuthReducerState {
    token?: string;
    isAuthenticated: boolean;
    waitForManualAuthInit: boolean;

    // todo remove during migration on hooks
    isTokenObtaining: boolean;
    errors?: StoreResponseErrors;
}

const initialState: AuthReducerState = {
    isAuthenticated: false,
    isTokenObtaining: false,
    waitForManualAuthInit: false,
};

export const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        // anon session
        obtainAnonymousToken: state => {
            state.isTokenObtaining = true;
        },
        obtainAnonymousTokenSuccess: (state, action: PayloadAction<{ token: string }>) => {
            state.token = action.payload.token;
            state.isTokenObtaining = false;
        },
        obtainAnonymousTokenFailure: state => {
            state.isTokenObtaining = false;
        },

        // login short IQT
        obtainSessionToken: state => {
            state.isTokenObtaining = true;
            state.errors = undefined;
        },
        obtainSessionTokenSuccess: (state, action: PayloadAction<{ token: string }>) => {
            state.isTokenObtaining = false;
            state.isAuthenticated = true;
            state.token = action.payload.token;
            state.errors = undefined;
        },
        obtainSessionTokenFailure: state => {
            state.isTokenObtaining = false;
            state.errors = undefined;
        },

        // login silent (hash from get params)
        silentLogin: (state, action: PayloadAction<PasswordlessLogin>) => {
            state.isTokenObtaining = true;
            state.errors = undefined;
        },
        silentLoginSuccess: (state, action: PayloadAction<{ token: string }>) => {
            state.isTokenObtaining = false;
            state.isAuthenticated = true;
            state.token = action.payload.token;
            state.errors = undefined;
        },
        silentLoginFailure: state => {
            state.isTokenObtaining = false;
            state.errors = undefined;
        },

        // login through SSO
        ssoLoginSuccess: (state, action: PayloadAction<{ token: string }>) => {
            state.isAuthenticated = true;
            state.token = action.payload.token;
            state.errors = undefined;
        },

        logout: () => {},

        // two factor
        setup2FaCompleted(state) {
            state.waitForManualAuthInit = false;
        },
    },
    extraReducers: builder => {
        builder.addMatcher(
            isAnyOf(
                api.endpoints.auth.matchFulfilled,
                api.endpoints.obtainAuthTokenCreate.matchFulfilled,
                api.endpoints.postV22FaVerify.matchFulfilled,
                api.endpoints.signupCreate.matchFulfilled,
            ),
            (state, { payload }) => {
                state.isTokenObtaining = false;
                state.isAuthenticated = true;
                state.token = payload.token;
                state.errors = undefined;

                // Complete authentication manually after saving recovery codes
                if ((payload as TwoFactorAuthVerifyResponse).backup_codes?.length) {
                    state.waitForManualAuthInit = true;
                }
            },
        );
        // builder.addMatcher(
        //     isAnyOf(
        //         contentApi.endpoints.getContent.matchPending,
        //         dailyPlayersApi.endpoints.dailyPlayersList.matchPending,
        //         cardsApi.endpoints.cardsList.matchPending,
        //         setsApi.endpoints.setsCreate.matchPending,
        //         usersApi.endpoints.usersRetrieve.matchPending,
        //     ),
        //     (state, { payload }) => {
        //         state.activeLoaders += 1;
        //     },
        // );
    },
});

export const authReducer = createPersistReducer(PersistReducerConfigs.auth, authSlice.reducer);
export const authActions = authSlice.actions;
